import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Mail, Phone, MapPin, Calendar, FileText, Clock, MessageSquare,
  ShieldCheck, Globe, FileCheck2, BadgeCheck, ClipboardCheck, Briefcase,
  Plus, ChevronRight, CheckCircle2, Circle, ArrowRight,
  Upload, XCircle, Download, ExternalLink, Eye, Webhook
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  STAGES, STAGE_MAP, stagePillClass, dbsPillClass, refsPillClass, rtwPillClass,
  fmtDate, fmtDateTime, daysUntil, initials, roleLabel
} from "@/lib/cc";
import Modal from "@/components/Modal";

const Ctx = createContext(null);

export function CandidateDrawerProvider({ children }) {
  const [openId, setOpenId] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const open = useCallback((id) => setOpenId(id), []);
  const close = useCallback(() => { setOpenId(null); setCandidate(null); }, []);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!openId) return;
    let alive = true;
    api.get(`/candidates/${openId}`).then(({ data }) => {
      if (alive) setCandidate(data);
    }).catch(() => toast.error("Could not load candidate"));
    return () => { alive = false; };
  }, [openId, refreshKey]);

  return (
    <Ctx.Provider value={{ open, close, refresh }}>
      {children}
      {openId && (
        <>
          <div className="cc-drawer-overlay" onClick={close} data-testid="drawer-overlay" />
          <CandidateDrawer candidate={candidate} onClose={close} onRefresh={refresh} />
        </>
      )}
    </Ctx.Provider>
  );
}

export const useCandidateDrawer = () => useContext(Ctx);

function CandidateDrawer({ candidate, onClose, onRefresh }) {
  const [tab, setTab] = useState("overview");
  if (!candidate) {
    return (
      <div className="cc-drawer p-8" data-testid="candidate-drawer">
        <div className="text-[var(--cc-text-soft)]">Loading candidate…</div>
      </div>
    );
  }
  const tabs = [
    { key: "overview", label: "Overview", icon: FileText },
    { key: "timeline", label: "Timeline", icon: Clock },
    { key: "interviews", label: "Interviews", icon: Briefcase },
    { key: "communications", label: "Comms", icon: MessageSquare },
    { key: "dbs", label: "DBS", icon: ShieldCheck },
    { key: "rtw", label: "RTW", icon: Globe },
    { key: "references", label: "References", icon: FileCheck2 },
    { key: "offer", label: "Offer", icon: BadgeCheck },
    { key: "onboarding", label: "Onboarding", icon: ClipboardCheck },
    { key: "notes", label: "Notes", icon: MessageSquare },
  ];

  return (
    <div className="cc-drawer" data-testid="candidate-drawer">
      <DrawerHeader candidate={candidate} onClose={onClose} onRefresh={onRefresh} />
      <div className="px-8 border-b border-[var(--cc-border)] flex items-center gap-2 overflow-x-auto cc-scroll">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`tab-btn flex items-center gap-1.5 ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
            data-testid={`drawer-tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="p-8">
        {tab === "overview" && <OverviewTab c={candidate} />}
        {tab === "timeline" && <TimelineTab c={candidate} />}
        {tab === "interviews" && <InterviewsTab c={candidate} onRefresh={onRefresh} />}
        {tab === "communications" && <CommunicationsTab c={candidate} />}
        {tab === "dbs" && <DBSTab c={candidate} onRefresh={onRefresh} />}
        {tab === "rtw" && <RTWTab c={candidate} onRefresh={onRefresh} />}
        {tab === "references" && <ReferencesTab c={candidate} onRefresh={onRefresh} />}
        {tab === "offer" && <OfferTab c={candidate} onRefresh={onRefresh} />}
        {tab === "onboarding" && <OnboardingTab c={candidate} onRefresh={onRefresh} />}
        {tab === "notes" && <NotesTab c={candidate} onRefresh={onRefresh} />}
      </div>
    </div>
  );
}

function DrawerHeader({ candidate, onClose, onRefresh }) {
  const [showReject, setShowReject] = useState(false);
  const moveStage = async (stage) => {
    if (stage === "rejected") { setShowReject(true); return; }
    try {
      await api.patch(`/candidates/${candidate.id}/stage`, { stage });
      toast.success(`Moved to ${STAGE_MAP[stage]}`);
      onRefresh();
    } catch { toast.error("Could not update stage"); }
  };
  return (
    <div className="p-8 pb-5 border-b border-[var(--cc-border)] bg-[var(--cc-surface)]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg"
               style={{ background: "var(--cc-primary)" }}>
            {initials(candidate.first_name, candidate.last_name)}
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold" data-testid="drawer-candidate-name">
              {candidate.first_name} {candidate.last_name}
            </h2>
            <div className="text-sm text-[var(--cc-text-soft)] mt-0.5">{candidate.job_title}</div>
            <div className="flex items-center gap-3 mt-2 text-xs text-[var(--cc-text-soft)]">
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{candidate.email}</span>
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{candidate.phone}</span>
              {candidate.cv_file?.file_id && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-[var(--cc-primary)] font-semibold underline"
                  data-testid="drawer-cv-link"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/admin/cv-presigned-url/${candidate.cv_file.file_id}`);
                      window.open(data.url, "_blank", "noopener");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not open CV");
                    }
                  }}
                >
                  <FileText className="w-3 h-3" /> View CV
                </button>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost" data-testid="drawer-close-button"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center justify-between mt-5">
        <span className={`pill ${stagePillClass(candidate.stage)}`} data-testid="drawer-stage-pill">
          <span className="pill-dot" style={{ background: "currentColor" }} />
          {STAGE_MAP[candidate.stage]}
        </span>
        <div className="flex items-center gap-2">
          <select className="cc-select py-1.5 text-xs w-44" value={candidate.stage} onChange={(e) => moveStage(e.target.value)} data-testid="drawer-stage-select">
            {STAGES.map((s) => <option key={s.key} value={s.key}>Move to {s.label}</option>)}
          </select>
        </div>
      </div>
      {showReject && <RejectModal cid={candidate.id} candidateName={`${candidate.first_name} ${candidate.last_name}`} onClose={() => setShowReject(false)} onDone={() => { setShowReject(false); onRefresh(); }} />}
    </div>
  );
}

function RejectModal({ cid, candidateName, onClose, onDone }) {
  const [reason, setReason] = useState("Not the right experience match for this role");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please provide a reason");
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/reject`, { reason, detailed_feedback: feedback });
      toast.success("Candidate rejected. Email sent.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testId="reject-modal" widthClass="max-w-md" zIndex={70}>
      <div id="reject-modal">
        <h2 className="font-display text-2xl font-bold mb-1">Reject candidate</h2>
        <p className="text-sm text-[var(--cc-text-soft)] mb-4">{candidateName} will be notified by email. Their record will be flagged for GDPR auto-deletion after 6 months.</p>
        <label className="label-eyebrow block mb-1">Reason</label>
        <select className="cc-select mb-3" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-modal-reason" id="reject-reason-select">
          <option>Not the right experience match for this role</option>
          <option>Did not meet minimum qualifications</option>
          <option>Stronger candidates progressed</option>
          <option>Right to Work could not be verified</option>
          <option>DBS check returned issues</option>
          <option>Withdrawn by candidate</option>
          <option>Other</option>
        </select>
        <label className="label-eyebrow block mb-1">Optional feedback to candidate</label>
        <textarea className="cc-textarea min-h-[80px] mb-4" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Constructive feedback…" data-testid="reject-modal-feedback" id="reject-feedback-textarea" />
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-danger" onClick={submit} disabled={busy} data-testid="reject-modal-submit" id="confirm-reject-button">{busy ? "Rejecting…" : "Reject & send email"}</button>
        </div>
      </div>
    </Modal>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="cc-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-dash font-bold text-base">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function OverviewTab({ c }) {
  return (
    <div className="space-y-5">
      <Card title="CV Summary">
        <p className="text-sm text-[var(--cc-text-soft)] leading-relaxed">{c.cv_summary}</p>
      </Card>
      <Card title="Work History">
        <div className="space-y-4">
          {c.work_history?.map((w, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-1 rounded-full bg-[var(--cc-secondary)]" />
              <div className="flex-1">
                <div className="font-semibold text-sm">{w.role}</div>
                <div className="text-xs text-[var(--cc-text-soft)]">{w.employer} · {w.start} → {w.end}</div>
                {w.description && <div className="text-sm mt-1 text-[var(--cc-text-soft)]">{w.description}</div>}
              </div>
            </div>
          ))}
          {!c.work_history?.length && <p className="text-sm text-[var(--cc-text-soft)]">No work history provided.</p>}
        </div>
      </Card>
      <Card title="Application Snapshot">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="label-eyebrow mb-1">Applied</div><div>{fmtDate(c.applied_at)}</div></div>
          <div><div className="label-eyebrow mb-1">Source</div><div>{c.source}</div></div>
          <div><div className="label-eyebrow mb-1">DBS</div><span className={`pill ${dbsPillClass(c.dbs?.status)}`}>{(c.dbs?.status || 'not started').replace('_', ' ')}</span></div>
          <div><div className="label-eyebrow mb-1">RTW</div><span className={`pill ${rtwPillClass(c.rtw?.status)}`}>{(c.rtw?.status || 'not started').replace('_', ' ')}</span></div>
        </div>
      </Card>
    </div>
  );
}

function TimelineTab({ c }) {
  return (
    <Card title="Activity Timeline">
      <div className="space-y-3">
        {[...(c.timeline || [])].reverse().map((t, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: "var(--cc-primary)" }} />
            <div className="flex-1">
              <div className="text-sm font-medium">{t.event}</div>
              <div className="text-xs text-[var(--cc-text-soft)]">{fmtDateTime(t.at)} · {t.by}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function InterviewsTab({ c, onRefresh }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [type, setType] = useState("in_person");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("14:00");
  const [duration, setDuration] = useState(45);
  const [venue, setVenue] = useState("");
  const [panel, setPanel] = useState("");
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    api.get("/admin/interview-templates").then(({ data }) => setTemplates(data || []));
  }, []);

  const filteredTemplates = templates.filter((t) => t.stage === type || (type === "panel" && t.stage === "in_person"));

  const schedule = async () => {
    if (!date) return toast.error("Pick a date");
    if (!time) return toast.error("Pick a time");
    try {
      const [h, m] = time.split(":");
      const dt = new Date(date);
      dt.setHours(Number(h), Number(m), 0, 0);
      await api.post(`/candidates/${c.id}/interviews`, {
        type,
        scheduled_at: dt.toISOString(),
        venue,
        panel: panel.split(",").map((p) => p.trim()).filter(Boolean),
        template_id: templateId || null,
        duration_minutes: Number(duration),
        instructions,
      });
      toast.success("Interview scheduled — invitation email sent");
      setShowSchedule(false); setDate(""); setTime("14:00"); setVenue(""); setPanel(""); setTemplateId(""); setInstructions("");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };

  return (
    <div className="space-y-5">
      <Card title="Interviews" action={
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setShowPhone(!showPhone)} data-testid="phone-screen-button">
            <Phone className="w-4 h-4" /> Phone screen
          </button>
          <button className="btn-primary" onClick={() => setShowSchedule(!showSchedule)} data-testid="schedule-interview-button">
            <Plus className="w-4 h-4" /> Schedule
          </button>
        </div>
      }>
        {showPhone && <PhoneScreenForm cid={c.id} onClose={() => setShowPhone(false)} onDone={() => { setShowPhone(false); onRefresh(); }} />}
        {showSchedule && (
          <div className="mb-5 p-4 rounded-lg bg-[var(--cc-surface)] space-y-3" data-testid="schedule-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1">Type</label>
                <select className="cc-select" value={type} onChange={(e) => { setType(e.target.value); setTemplateId(""); }} data-testid="schedule-type-select">
                  <option value="phone_screen">Phone Screen</option>
                  <option value="in_person">In Person</option>
                  <option value="panel">Panel</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Template</label>
                <select className="cc-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="schedule-template-select">
                  <option value="">No template</option>
                  {filteredTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} ({(t.questions || []).length} questions)</option>)}
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Date</label>
                <input type="date" className="cc-input" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="schedule-date-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Start time</label>
                <input type="time" className="cc-input" value={time} onChange={(e) => setTime(e.target.value)} data-testid="schedule-time-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Duration (mins)</label>
                <input type="number" min={15} step={15} className="cc-input" value={duration} onChange={(e) => setDuration(e.target.value)} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Venue / link</label>
                <input className="cc-input" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Office, Zoom, Teams…" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Panel (comma-separated, up to 3 interviewers)</label>
              <input className="cc-input" value={panel} onChange={(e) => setPanel(e.target.value)} placeholder="Sarah Mitchell, Priya Sharma, David Williams" data-testid="schedule-panel-input" />
              <p className="text-[11px] text-[var(--cc-text-soft)] mt-1">Each panel member scores the candidate independently using the chosen template.</p>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Instructions (optional)</label>
              <textarea className="cc-textarea min-h-[60px]" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="What the candidate should prepare or bring…" />
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setShowSchedule(false)}>Cancel</button>
              <button className="btn-primary" onClick={schedule} data-testid="schedule-confirm-button">Schedule & send invitation</button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {c.interviews?.length === 0 && <p className="text-sm text-[var(--cc-text-soft)]">No interviews scheduled.</p>}
          {c.interviews?.map((iv) => (
            <InterviewRow key={iv.id} cid={c.id} iv={iv} templates={templates} onRefresh={onRefresh} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function PhoneScreenForm({ cid, onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [interviewer, setInterviewer] = useState("Sarah Mitchell");
  const [duration, setDuration] = useState(30);
  const [instructions, setInstructions] = useState("");
  // Calendar-style slot picker: pick date + time, system generates 3 sequential slots; add more as needed
  const [slotRows, setSlotRows] = useState([
    { date: "", time: "10:00" },
    { date: "", time: "14:00" },
    { date: "", time: "11:00" },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/interview-templates?status=active").then(({ data }) => setTemplates(data.filter((t) => t.stage === "phone_screen")));
  }, []);

  const updateRow = (i, key, val) => {
    const next = [...slotRows]; next[i] = { ...next[i], [key]: val }; setSlotRows(next);
  };
  const buildISO = (row) => {
    if (!row.date || !row.time) return null;
    const dt = new Date(row.date);
    const [h, m] = row.time.split(":");
    dt.setHours(Number(h), Number(m), 0, 0);
    return dt.toISOString();
  };

  const submit = async () => {
    const isoSlots = slotRows.map(buildISO).filter(Boolean);
    if (isoSlots.length < 3) return toast.error("Offer at least 3 valid date+time slots");
    if (isoSlots.length > 5) return toast.error("Maximum 5 slots");
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/phone-screen`, {
        interviewer, duration_minutes: Number(duration),
        instructions, template_id: templateId || null,
        slots: isoSlots,
      });
      toast.success("Invitation sent — email + SMS dispatched");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-5 p-4 rounded-lg bg-[var(--cc-surface)] space-y-3" data-testid="phone-screen-form">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-eyebrow block mb-1">Template</label>
          <select className="cc-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="phone-screen-template-select">
            <option value="">No template</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></div>
        <div><label className="label-eyebrow block mb-1">Interviewer</label>
          <input className="cc-input" value={interviewer} onChange={(e) => setInterviewer(e.target.value)} data-testid="phone-screen-interviewer-input" /></div>
        <div><label className="label-eyebrow block mb-1">Duration (mins)</label>
          <input type="number" className="cc-input" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="phone-screen-duration-input" /></div>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Offer 3–5 time slots</label>
        <p className="text-[11px] text-[var(--cc-text-soft)] mb-2">Pick a date and time per slot. The candidate will see them formatted as "Monday 20 May at 14:00".</p>
        <div className="space-y-2">
          {slotRows.map((row, i) => {
            const iso = buildISO(row);
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <input type="date" className="cc-input" value={row.date} min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => updateRow(i, "date", e.target.value)} data-testid={`phone-screen-slot-date-${i}`} />
                </div>
                <div className="col-span-3">
                  <input type="time" className="cc-input" value={row.time}
                    onChange={(e) => updateRow(i, "time", e.target.value)} data-testid={`phone-screen-slot-time-${i}`} />
                </div>
                <div className="col-span-4 text-xs text-[var(--cc-text-soft)] truncate">
                  {iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="col-span-1 text-right">
                  {slotRows.length > 3 && (
                    <button type="button" className="btn-ghost text-[11px] px-2" onClick={() => setSlotRows(slotRows.filter((_, k) => k !== i))} title="Remove slot">×</button>
                  )}
                </div>
              </div>
            );
          })}
          {slotRows.length < 5 && <button className="btn-ghost text-xs" onClick={() => setSlotRows([...slotRows, { date: "", time: "10:00" }])} data-testid="phone-screen-add-slot">+ Add slot</button>}
        </div>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Instructions (optional)</label>
        <textarea className="cc-textarea min-h-[60px]" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="What the candidate should expect…" />
      </div>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="send-phone-screen-button">{busy ? "Sending…" : "Send invitation"}</button>
      </div>
    </div>
  );
}

function CommunicationsTab({ c }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/candidates/${c.id}/communications`).then(({ data }) => setRows(data));
  }, [c.id]);
  return (
    <Card title={`Communications (${rows.length})`}>
      <p className="text-xs text-[var(--cc-text-soft)] mb-4">Every email and SMS sent to {c.first_name}, in time order.</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="border border-[var(--cc-border)] rounded-lg p-3" data-testid={`comm-row-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-xs">
                <span className={`pill ${r.channel === "email" ? "pill-info" : r.channel === "sms" ? "pill-sage" : "pill-neutral"}`}>{r.channel}</span>
                <span className="pill pill-neutral !text-[10px]">{r.tag || "—"}</span>
                <span className={`pill !text-[10px] ${r.status === "sent" ? "pill-success" : r.status === "failed" ? "pill-danger" : "pill-warning"}`}>{r.status}</span>
              </div>
              <span className="text-xs text-[var(--cc-text-soft)]">{fmtDateTime(r.at)}</span>
            </div>
            <div className="text-sm font-semibold">{r.subject || "(no subject)"}</div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">to {r.to}</div>
            <div className="text-sm mt-2 line-clamp-3">{r.body}</div>
            {r.error && <div className="text-xs text-[var(--cc-accent)] mt-2">⚠ {r.error}</div>}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[var(--cc-text-soft)]">No communications yet.</p>}
      </div>
    </Card>
  );
}

function CancelInterviewButton({ cid, iv, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("interviewer_unavailable");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/interviews/${iv.id}/cancel`, { reason, explanation });
      toast.success("Interview cancelled — candidate notified");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button className="btn-ghost py-1.5 px-2 text-xs text-[var(--cc-accent)]" onClick={() => setOpen(true)} data-testid={`cancel-interview-${iv.id}`}>
      <XCircle className="w-3.5 h-3.5" /> Cancel
    </button>
  );
  return (
    <div className="absolute right-4 mt-10 z-20 w-72 cc-card p-4 space-y-2 shadow-lg" data-testid={`cancel-form-${iv.id}`}>
      <div className="label-eyebrow">Cancel this interview</div>
      <select className="cc-select py-1.5" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`cancel-reason-${iv.id}`}>
        <option value="interviewer_unavailable">Interviewer unavailable</option>
        <option value="position_filled">Position filled</option>
        <option value="candidate_request">Candidate request</option>
        <option value="technical_issue">Technical issue</option>
        <option value="other">Other</option>
      </select>
      <textarea className="cc-textarea min-h-[60px] text-xs" maxLength={200} value={explanation} onChange={(e) => setExplanation(e.target.value)}
                placeholder="Optional note for the candidate (max 200 chars)" />
      <div className="flex gap-2 justify-end">
        <button className="btn-ghost text-xs" onClick={() => setOpen(false)}>Back</button>
        <button className="btn-primary text-xs" onClick={submit} disabled={busy} data-testid={`cancel-confirm-${iv.id}`}>{busy ? "Cancelling…" : "Confirm cancel"}</button>
      </div>
    </div>
  );
}

function InterviewRow({ cid, iv, templates = [], onRefresh }) {
  const [openScore, setOpenScore] = useState(false);
  const template = useMemo(() => templates.find((t) => t.id === iv.template_id) || null, [templates, iv.template_id]);
  const questions = useMemo(() => {
    if (template?.questions?.length) return template.questions.map((q) => q.text || q.competency || q);
    if (iv.type === "phone_screen") return ["Communication", "Experience", "Motivation"];
    return ["Person-centred care", "Safeguarding", "CQC awareness", "Teamwork"];
  }, [template, iv.type]);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  useEffect(() => { setScores(Object.fromEntries(questions.map((q) => [q, 3]))); }, [questions]);
  const avg = Object.values(scores).length ? Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length : 0;

  const submit = async () => {
    try {
      await api.post(`/candidates/${cid}/interviews/score`, { interview_id: iv.id, scores, notes });
      toast.success("Score submitted");
      setOpenScore(false);
      onRefresh();
    } catch { toast.error("Could not submit"); }
  };

  const scoresList = iv.scores || [];
  const overallAvg = scoresList.length ? (scoresList.reduce((a, s) => a + (s.overall || 0), 0) / scoresList.length).toFixed(2) : null;

  return (
    <div className="border border-[var(--cc-border)] rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold capitalize">{iv.type.replace("_", " ")}</div>
          <div className="text-xs text-[var(--cc-text-soft)] mt-0.5">{fmtDateTime(iv.scheduled_at)} · {iv.venue || "—"}</div>
          {iv.panel?.length > 0 && <div className="text-xs mt-1">Panel: {iv.panel.join(", ")}</div>}
          {template && <div className="text-xs mt-1 text-[var(--cc-primary)]">Template: {template.name} ({(template.questions || []).length} questions)</div>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`pill ${iv.status === "completed" ? "pill-success" : iv.status === "cancelled" ? "pill-danger" : "pill-info"}`}>{iv.status}</span>
          {iv.status !== "cancelled" && (
            <Link to={`/app/candidates/${cid}/interviews/${iv.id}/pack`} className="btn-ghost py-1.5 px-2 text-xs" title="Interview pack (30-sec prep)" data-testid={`pack-link-${iv.id}`}>
              <FileText className="w-3.5 h-3.5" /> Pack
            </Link>
          )}
          {(iv.status === "scheduled" || iv.status === "pending_confirmation") && (
            <CancelInterviewButton cid={cid} iv={iv} onDone={onRefresh} />
          )}
          {(iv.status === "scheduled" || iv.status === "completed") && (
            <button className="btn-secondary py-1.5 px-3 text-xs" onClick={() => setOpenScore(!openScore)} data-testid={`score-interview-${iv.id}`}>
              {scoresList.length ? `+ Score (${scoresList.length}/3)` : "Score"}
            </button>
          )}
        </div>
      </div>

      {scoresList.length > 0 && (
        <div className="mt-4 cc-divider pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Scorecards ({scoresList.length} interviewer{scoresList.length === 1 ? "" : "s"})</div>
            {overallAvg && <div className="text-sm">Panel average: <strong className="text-[var(--cc-primary)]">{overallAvg}/5</strong></div>}
          </div>
          <div className={scoresList.length > 1 ? "grid grid-cols-1 md:grid-cols-2 gap-3" : ""}>
            {scoresList.map((sc, i) => (
              <div key={i} className="bg-[var(--cc-surface)] rounded-lg p-3" data-testid={`scorecard-${i}`}>
                <div className="flex justify-between text-sm font-semibold">
                  <span>{sc.interviewer} <span className="text-xs font-normal text-[var(--cc-text-soft)]">· {roleLabel(sc.interviewer_role)}</span></span>
                  <span className="text-[var(--cc-primary)]">{sc.overall}/5</span>
                </div>
                <div className="grid grid-cols-1 gap-x-3 gap-y-1 mt-2 text-xs">
                  {Object.entries(sc.scores).map(([q, v]) => (
                    <div key={q} className="flex justify-between"><span className="text-[var(--cc-text-soft)] truncate pr-2">{q}</span><span className="font-semibold whitespace-nowrap">{v}/5</span></div>
                  ))}
                </div>
                {sc.notes && <div className="text-xs mt-2 text-[var(--cc-text-soft)] italic">"{sc.notes}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {openScore && (
        <div className="mt-4 cc-divider pt-4 space-y-3" data-testid={`score-form-${iv.id}`}>
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">{template ? `Scoring rubric — ${template.name}` : "Scoring rubric (1-5)"}</div>
            {scoresList.length >= 3 && <span className="text-xs text-[var(--cc-accent)]">Note: 3+ scorecards already on file</span>}
          </div>
          {template?.competencies?.length > 0 && (
            <div className="text-xs text-[var(--cc-text-soft)]">Competencies: {template.competencies.join(" · ")}</div>
          )}
          {questions.map((q) => (
            <div key={q} className="border border-[var(--cc-border)] rounded-lg p-3">
              <div className="text-sm mb-2">{q}</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} className={`rating-pill ${scores[q] === n ? "selected" : ""}`}
                    onClick={() => setScores({ ...scores, [q]: n })}
                    data-testid={`rating-${iv.id}-${n}`}>{n}</button>
                ))}
              </div>
            </div>
          ))}
          <textarea className="cc-textarea min-h-[60px]" placeholder="Notes / evidence" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex items-center justify-between">
            <div className="text-sm">Your average: <strong>{avg.toFixed(2)}/5</strong></div>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => setOpenScore(false)}>Cancel</button>
              <button className="btn-primary" onClick={submit} data-testid={`submit-score-${iv.id}`}>Submit Score</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DbsRequestCard({ cid }) {
  const [open, setOpen] = useState(false);
  const [dbsType, setDbsType] = useState("Enhanced");
  const [deadline, setDeadline] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [instructions, setInstructions] = useState("");
  const [hrNotes, setHrNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);

  const load = () => api.get(`/candidates/${cid}/dbs-requests`).then(({ data }) => setRequests(data || [])).catch(() => {});
  useEffect(() => { load(); }, [cid]);

  const send = async () => {
    if (!deadline) return toast.error("Pick a deadline");
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/dbs-request`, { dbs_type: dbsType, deadline, custom_instructions: instructions, hr_notes: hrNotes });
      toast.success("DBS request sent to candidate");
      setOpen(false); setInstructions(""); setHrNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card title="DBS Request" action={<button className="btn-primary text-xs" onClick={() => setOpen(!open)} data-testid="dbs-request-toggle">{open ? "Cancel" : "Request DBS Check"}</button>}>
      {open ? (
        <div className="p-3 rounded-lg bg-[var(--cc-surface)] space-y-2" data-testid="dbs-request-form">
          <div className="grid grid-cols-2 gap-2">
            <select className="cc-select py-1.5" value={dbsType} onChange={(e) => setDbsType(e.target.value)} data-testid="dbs-request-type">
              <option value="Enhanced">Enhanced DBS (recommended for care)</option>
              <option value="Standard">Standard DBS</option>
              <option value="Other">Other</option>
            </select>
            <input type="date" className="cc-input py-1.5" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="dbs-request-deadline" />
          </div>
          <textarea className="cc-textarea min-h-[60px] text-sm" placeholder="Optional instructions for the candidate" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="dbs-request-instructions" />
          <textarea className="cc-textarea min-h-[40px] text-xs" placeholder="HR-only notes (not emailed)" value={hrNotes} onChange={(e) => setHrNotes(e.target.value)} />
          <div className="flex justify-end">
            <button className="btn-primary text-xs" onClick={send} disabled={busy} data-testid="dbs-request-send">{busy ? "Sending…" : "Send DBS Request"}</button>
          </div>
        </div>
      ) : (
        requests.length === 0 ? <div className="text-sm text-[var(--cc-text-soft)]">No DBS request has been sent yet.</div> : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="border border-[var(--cc-border)] rounded-lg p-3 flex items-center justify-between" data-testid={`dbs-request-${r.id}`}>
                <div className="text-sm">
                  <strong>{r.dbs_type}</strong> · Deadline {fmtDate(r.deadline)}
                  <div className="text-xs text-[var(--cc-text-soft)]">Requested {fmtDate(r.requested_at)} by {r.requested_by_name}</div>
                </div>
                <span className={`pill ${r.status === "verified" ? "pill-success" : r.status === "rejected" ? "pill-danger" : "pill-warning"}`}>{r.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        )
      )}
    </Card>
  );
}

function DbsEvidenceCard({ cid }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Enhanced");
  const [ref, setRef] = useState("");
  const [issued, setIssued] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState({});

  const load = () => api.get(`/candidates/${cid}/dbs`).then(({ data }) => setDocs(data || []));
  useEffect(() => { load(); }, [cid]);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF/JPG/PNG");
    if (!ref || !issued) return toast.error("Reference and issue date are required");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("disclosure_type", type);
    fd.append("reference_number", ref);
    fd.append("issue_date", issued);
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/dbs/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("DBS certificate uploaded");
      setOpen(false); setFile(null); setRef(""); setIssued(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const verify = async (d, status, notes) => {
    try {
      await api.post(`/candidates/${cid}/dbs/${d.id}/verify`, { verification_status: status, notes });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEvidence = async (d) => {
    try {
      const { data } = await api.get(`/candidates/${cid}/dbs/${d.id}/url`);
      window.open(data.url, "_blank", "noopener");
    } catch { toast.error("Could not open evidence"); }
  };

  return (
    <Card title="DBS Evidence" action={<button className="btn-secondary text-xs" onClick={() => setOpen(!open)} data-testid="dbs-upload-toggle"><Upload className="w-3.5 h-3.5" /> Upload</button>}>
      {open && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--cc-surface)] space-y-2" data-testid="dbs-upload-form">
          <div className="grid grid-cols-3 gap-2">
            <select className="cc-select py-1.5" value={type} onChange={(e) => setType(e.target.value)} data-testid="dbs-type-select">
              <option value="Enhanced">Enhanced</option>
              <option value="Standard">Standard</option>
            </select>
            <input className="cc-input py-1.5" placeholder="Reference #" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="dbs-ref-input" />
            <input type="date" className="cc-input py-1.5" value={issued} onChange={(e) => setIssued(e.target.value)} data-testid="dbs-issue-input" />
          </div>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} className="text-xs" data-testid="dbs-file-input" />
          <div className="text-[11px] text-[var(--cc-text-soft)]">PDF/JPG/PNG · max 20 MB · expiry auto-calculated (issue date + 3 years).</div>
          <div className="flex justify-end gap-2">
            <button className="btn-ghost text-xs" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary text-xs" onClick={upload} disabled={busy} data-testid="dbs-upload-submit">{busy ? "Uploading…" : "Upload"}</button>
          </div>
        </div>
      )}
      {docs.length === 0 ? <div className="text-sm text-[var(--cc-text-soft)]">No DBS certificate uploaded yet.</div> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="border border-[var(--cc-border)] rounded-lg p-3" data-testid={`dbs-doc-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <strong>{d.disclosure_type}</strong> · Ref {d.reference_number}<br/>
                  <span className="text-xs text-[var(--cc-text-soft)]">Issued {fmtDate(d.issue_date)} · expires {fmtDate(d.expiry_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`pill ${d.verification_status === "verified" ? "pill-success" : d.verification_status === "rejected" ? "pill-danger" : "pill-warning"}`}>{d.verification_status.replace("_", " ")}</span>
                  <a className="btn-ghost text-xs py-1" href="#" onClick={(e) => { e.preventDefault(); openEvidence(d); }} title="Open signed URL (15-min)" data-testid={`dbs-open-${d.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>
                </div>
              </div>
              {d.verification_status === "pending" && (
                <div className="mt-2 flex gap-2 justify-end">
                  <button className="btn-ghost text-xs text-red-600" onClick={() => verify(d, "rejected", "")} data-testid={`dbs-reject-${d.id}`}>Not clear</button>
                  <button className="btn-ghost text-xs text-amber-700" onClick={() => verify(d, "cannot_verify", "")}>Cannot verify</button>
                  <button className="btn-primary text-xs" onClick={() => verify(d, "verified", "Verified by HR")} data-testid={`dbs-verify-${d.id}`}>Mark clear</button>
                </div>
              )}
              {d.verification_notes && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1 italic">"{d.verification_notes}"</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RtwEvidenceCard({ cid }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("passport");
  const [visa, setVisa] = useState("british");
  const [num, setNum] = useState("");
  const [issued, setIssued] = useState("");
  const [exp, setExp] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/candidates/${cid}/rtw`).then(({ data }) => setDocs(data || []));
  useEffect(() => { load(); }, [cid]);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF/JPG/PNG");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("visa_category", visa);
    fd.append("document_type", docType);
    fd.append("document_number", num);
    fd.append("issue_date", issued);
    fd.append("expiry_date", exp);
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/rtw/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("RTW document uploaded");
      setOpen(false); setFile(null); setNum(""); setIssued(""); setExp(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const verify = async (d, status, notes) => {
    try {
      await api.post(`/candidates/${cid}/rtw/${d.id}/verify`, { verification_status: status, notes });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEvidence = async (d) => {
    try {
      const { data } = await api.get(`/candidates/${cid}/rtw/${d.id}/url`);
      window.open(data.url, "_blank", "noopener");
    } catch { toast.error("Could not open evidence"); }
  };

  return (
    <Card title="Right-to-Work Evidence" action={<button className="btn-secondary text-xs" onClick={() => setOpen(!open)} data-testid="rtw-upload-toggle"><Upload className="w-3.5 h-3.5" /> Upload</button>}>
      {open && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--cc-surface)] space-y-2" data-testid="rtw-upload-form">
          <div className="grid grid-cols-2 gap-2">
            <select className="cc-select py-1.5" value={visa} onChange={(e) => setVisa(e.target.value)} data-testid="rtw-visa-select">
              <option value="british">British / Settled</option>
              <option value="bn_o">BN(O) Visa</option>
              <option value="skilled_worker">Skilled Worker</option>
              <option value="health_care">Health & Care Worker</option>
              <option value="student">Student Visa</option>
              <option value="graduate">Graduate Visa</option>
              <option value="spouse">Spouse/Family</option>
              <option value="working_holiday">Working Holiday</option>
              <option value="other">Other</option>
            </select>
            <select className="cc-select py-1.5" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="rtw-doctype-select">
              <option value="passport">Passport</option>
              <option value="visa">Visa</option>
              <option value="brp_card">BRP Card</option>
              <option value="ukvi_letter">UKVI Letter</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input className="cc-input py-1.5" placeholder="Document #" value={num} onChange={(e) => setNum(e.target.value)} />
            <input type="date" className="cc-input py-1.5" placeholder="Issue" value={issued} onChange={(e) => setIssued(e.target.value)} />
            <input type="date" className="cc-input py-1.5" placeholder="Expiry" value={exp} onChange={(e) => setExp(e.target.value)} />
          </div>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} className="text-xs" data-testid="rtw-file-input" />
          <div className="flex justify-end gap-2">
            <button className="btn-ghost text-xs" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary text-xs" onClick={upload} disabled={busy} data-testid="rtw-upload-submit">{busy ? "Uploading…" : "Upload"}</button>
          </div>
        </div>
      )}
      {docs.length === 0 ? <div className="text-sm text-[var(--cc-text-soft)]">No RTW documents uploaded yet.</div> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="border border-[var(--cc-border)] rounded-lg p-3" data-testid={`rtw-doc-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <strong className="capitalize">{d.document_type.replace("_", " ")}</strong>{d.document_number ? ` · ${d.document_number}` : ""}<br/>
                  <span className="text-xs text-[var(--cc-text-soft)]">{d.visa_category} {d.issue_date ? `· issued ${fmtDate(d.issue_date)}` : ""} {d.expiry_date ? `· expires ${fmtDate(d.expiry_date)}` : ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`pill ${d.verification_status === "verified" ? "pill-success" : d.verification_status === "not_eligible" || d.verification_status === "expired" ? "pill-danger" : "pill-warning"}`}>{d.verification_status.replace("_", " ")}</span>
                  <button className="btn-ghost text-xs py-1" onClick={() => openEvidence(d)} title="Open signed URL (15-min)" data-testid={`rtw-open-${d.id}`}><ExternalLink className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {d.verification_status === "pending" && (
                <div className="mt-2 flex gap-2 justify-end">
                  <button className="btn-ghost text-xs text-red-600" onClick={() => verify(d, "not_eligible", "")} data-testid={`rtw-reject-${d.id}`}>Not eligible</button>
                  <button className="btn-ghost text-xs text-amber-700" onClick={() => verify(d, "needs_more_info", "")}>Needs info</button>
                  <button className="btn-primary text-xs" onClick={() => verify(d, "verified", "Verified by HR")} data-testid={`rtw-verify-${d.id}`}>Verify</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DBSTab({ c, onRefresh }) {
  const d = c.dbs || {};
  const [status, setStatus] = useState(d.status || "not_started");
  const [reference, setReference] = useState(d.reference || "");
  const [issued, setIssued] = useState(d.issued_date?.slice(0, 10) || "");
  const [expiry, setExpiry] = useState(d.expiry_date?.slice(0, 10) || "");
  const [updateSvc, setUpdateSvc] = useState(d.on_update_service || false);

  const save = async () => {
    try {
      await api.patch(`/candidates/${c.id}/dbs`, {
        status, reference, issued_date: issued ? new Date(issued).toISOString() : null,
        expiry_date: expiry ? new Date(expiry).toISOString() : null, on_update_service: updateSvc,
      });
      toast.success("DBS updated");
      onRefresh();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="space-y-5">
      <DbsRequestCard cid={c.id} />
      <DbsEvidenceCard cid={c.id} />
      <Card title="DBS Check">
      <div className="mb-4">
        <span className={`pill ${dbsPillClass(d.status)}`} data-testid="dbs-status-pill">{(d.status || "not started").replace("_", " ")}</span>
        {expiry && <span className="ml-2 text-xs text-[var(--cc-text-soft)]">Expires {fmtDate(expiry)} · {daysUntil(expiry)} days</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Status</label>
          <select className="cc-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dbs-status-select">
            <option value="not_started">Not Started</option>
            <option value="sent">Sent</option>
            <option value="in_progress">In Progress</option>
            <option value="received">Received</option>
            <option value="clear">Clear</option>
            <option value="not_clear">Not Clear</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Reference</label>
          <input className="cc-input" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Issued date</label>
          <input type="date" className="cc-input" value={issued} onChange={(e) => setIssued(e.target.value)} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Expiry date</label>
          <input type="date" className="cc-input" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" checked={updateSvc} onChange={(e) => setUpdateSvc(e.target.checked)} />
        Enrolled on DBS Update Service (annual auto-check)
      </label>
      <div className="flex justify-end mt-4">
        <button className="btn-primary" onClick={save} data-testid="dbs-save-button">Save</button>
      </div>
    </Card>
    </div>
  );
}

const VISA_CATEGORIES = [
  "Skilled Worker", "Health & Care Worker Visa", "Senior or Specialist Worker",
  "Graduate Visa", "Student Visa", "Dependant Visa", "Settled / Pre-settled", "Other",
];

function RTWTab({ c, onRefresh }) {
  const r = c.rtw || {};
  const [nat, setNat] = useState(r.nationality_status || "british");
  const [cat, setCat] = useState(r.visa_category || "");
  const [num, setNum] = useState(r.visa_number || "");
  const [exp, setExp] = useState(r.visa_expiry?.slice(0, 10) || "");
  const [verified, setVerified] = useState(!!r.documents_verified);
  const [status, setStatus] = useState(r.status || "not_started");
  const [notes, setNotes] = useState(r.notes || "");

  const save = async () => {
    try {
      await api.patch(`/candidates/${c.id}/rtw`, {
        nationality_status: nat, visa_category: cat || null, visa_number: num || null,
        visa_expiry: exp ? new Date(exp).toISOString() : null,
        documents_verified: verified, status, notes,
      });
      toast.success("RTW updated");
      onRefresh();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="space-y-5">
      <RtwEvidenceCard cid={c.id} />
      <Card title="Right to Work">
      <div className="flex items-center gap-2 mb-4">
        <button className={`btn-secondary ${nat === "british" ? "!bg-[var(--cc-primary)] !text-white" : ""}`} onClick={() => setNat("british")} data-testid="rtw-british-button">British / Settled</button>
        <button className={`btn-secondary ${nat === "immigrant" ? "!bg-[var(--cc-primary)] !text-white" : ""}`} onClick={() => setNat("immigrant")} data-testid="rtw-immigrant-button">Visa holder</button>
      </div>

      {nat === "immigrant" && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-eyebrow block mb-1">Visa category</label>
            <select className="cc-select" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="rtw-visa-category">
              <option value="">Select…</option>
              {VISA_CATEGORIES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Visa number</label>
            <input className="cc-input" value={num} onChange={(e) => setNum(e.target.value)} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Expiry</label>
            <input type="date" className="cc-input" value={exp} onChange={(e) => setExp(e.target.value)} />
            {exp && daysUntil(exp) < 90 && <div className="text-xs mt-1 text-[var(--cc-accent)]">Expires in {daysUntil(exp)} days</div>}
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Status</label>
            <select className="cc-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rtw-status-select">
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      )}
      {nat === "british" && (
        <div className="mb-3">
          <label className="label-eyebrow block mb-1">Status</label>
          <select className="cc-select max-w-xs" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      )}

      <label className="flex items-center gap-2 mb-3 text-sm">
        <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} data-testid="rtw-documents-verified" />
        Documents verified (passport / share code / visa letter)
      </label>
      <textarea className="cc-textarea min-h-[60px]" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex justify-end mt-4">
        <button className="btn-primary" onClick={save} data-testid="rtw-save-button">Save</button>
      </div>
    </Card>
    </div>
  );
}

function ReferencesTab({ c, onRefresh }) {
  const refs = c.references || [];
  const verified = refs.filter((r) => r.status === "verified" || r.status === "received").length;
  const [showAdd, setShowAdd] = useState(false);
  const canAdd = refs.length < 3;

  return (
    <>
      <Card title={`References (${verified}/${refs.length} received)`} action={
        <button
          className="btn-primary text-xs"
          onClick={() => setShowAdd(true)}
          disabled={!canAdd}
          title={canAdd ? "Add a referee (max 3)" : "Maximum 3 referees reached — remove one first"}
          data-testid="add-reference-button"
        >
          <Plus className="w-3.5 h-3.5" /> Add referee
        </button>
      }>
        <div className="space-y-3">
          {refs.map((r, i) => (
            <ReferenceRow key={i} cid={c.id} idx={i} r={r} onRefresh={onRefresh} />
          ))}
          {refs.length === 0 && <p className="text-sm text-[var(--cc-text-soft)]">No references on file. Click <strong>Add referee</strong> above to invite the first referee.</p>}
          <p className="text-[10px] text-[var(--cc-text-soft)]">CQC Safer Recruitment: minimum 2, maximum 3 references per candidate. {refs.length}/3 added.</p>
        </div>
      </Card>
      {showAdd && (
        <AddRefereeModal cid={c.id} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); onRefresh(); }} />
      )}
    </>
  );
}

function AddRefereeModal({ cid, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    relationship: "direct_manager", auto_send_request: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Name and email are required");
    }
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/references`, form);
      toast.success(form.auto_send_request
        ? "Referee added — request email sent"
        : "Referee added — you can send the request later from the row");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add referee");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testId="add-referee-modal" widthClass="max-w-lg" zIndex={70}>
      <h3 className="font-display text-xl font-bold mb-3">Add a referee</h3>
      <p className="text-sm text-[var(--cc-text-soft)] mb-4">
        Add the referee's contact details. You can send the secure online reference-form link immediately, or save now and send later.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label-eyebrow block mb-1">Full name *</label>
          <input className="cc-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="add-ref-name" />
        </div>
        <div className="col-span-2">
          <label className="label-eyebrow block mb-1">Email *</label>
          <input className="cc-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="add-ref-email" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Phone</label>
          <input className="cc-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="add-ref-phone" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Company</label>
          <input className="cc-input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="add-ref-company" />
        </div>
        <div className="col-span-2">
          <label className="label-eyebrow block mb-1">Relationship</label>
          <select className="cc-select" value={form.relationship} onChange={(e) => set("relationship", e.target.value)} data-testid="add-ref-relationship">
            <option value="direct_manager">Direct Manager</option>
            <option value="team_lead">Team Lead / Supervisor</option>
            <option value="hr_manager">HR Manager</option>
            <option value="colleague">Colleague</option>
            <option value="client">Client / Customer</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer mt-4">
        <input type="checkbox" checked={form.auto_send_request} onChange={(e) => set("auto_send_request", e.target.checked)} data-testid="add-ref-autosend" />
        Email the referee the reference-request link immediately
      </label>
      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-ghost" onClick={onClose} disabled={busy} data-testid="add-ref-cancel">Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="add-ref-submit">
          {busy ? "Adding…" : "Add referee"}
        </button>
      </div>
    </Modal>
  );
}

const VERDICT_BADGE = {
  acceptable: { label: "Acceptable", bg: "#e8f0ea", fg: "#1f4d36" },
  concerns: { label: "Concerns", bg: "#fef5e7", fg: "#7a4b00" },
  unsatisfactory: { label: "Unsatisfactory", bg: "#fef3f2", fg: "#7a271a" },
};

function ReferenceRow({ cid, idx, r, onRefresh }) {
  const [status, setStatus] = useState(r.status);
  const [feedback, setFeedback] = useState(r.feedback || "");
  const [reemploy, setReemploy] = useState(r.would_reemploy);
  const [expanded, setExpanded] = useState(false);
  const [verdict, setVerdict] = useState(r.assessment?.verdict || "");
  const [assessComment, setAssessComment] = useState(r.assessment?.comments || "");
  const [action, setAction] = useState(r.assessment?.action_taken || "none");
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [upFile, setUpFile] = useState(null);
  const [upComments, setUpComments] = useState("");
  const [upBusy, setUpBusy] = useState(false);
  const fr = r.form_response || null;
  const manualEvidence = r.manual_evidence || [];
  const isEmailUpload = r.received_via === "email_upload";

  const save = async () => {
    try {
      await api.patch(`/candidates/${cid}/references/${idx}`, { status, feedback, would_reemploy: reemploy });
      toast.success("Reference updated");
      onRefresh();
    } catch { toast.error("Could not update"); }
  };
  const requestRef = async () => {
    try {
      await api.post(`/candidates/${cid}/references/${idx}/request`);
      toast.success("Reference request email sent");
      onRefresh();
    } catch { toast.error("Failed"); }
  };
  const removeRef = async () => {
    if (!window.confirm(`Remove ${r.name} from this candidate's references? This cannot be undone.`)) return;
    try {
      await api.delete(`/candidates/${cid}/references/${idx}`);
      toast.success("Referee removed");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove referee");
    }
  };
  const submitAssessment = async () => {
    if (!verdict) return toast.error("Pick an assessment verdict");
    setBusy(true);
    try {
      await api.post(`/candidates/${cid}/references/${idx}/assess`, {
        verdict, comments: assessComment, action_taken: action,
      });
      toast.success("Assessment saved");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assess");
    } finally { setBusy(false); }
  };

  const uploadManual = async () => {
    if (!upFile) return toast.error("Choose a file (PDF / DOCX / image / .eml)");
    const fd = new FormData();
    fd.append("file", upFile);
    fd.append("comments", upComments);
    setUpBusy(true);
    try {
      await api.post(`/candidates/${cid}/references/${idx}/upload`, fd);
      toast.success("Reference uploaded — marked as received");
      setShowUpload(false); setUpFile(null); setUpComments("");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUpBusy(false); }
  };

  const openManual = async (file_id) => {
    try {
      const { data } = await api.get(`/candidates/${cid}/references/${idx}/upload/${file_id}/url`);
      window.open(data.url, "_blank", "noopener");
    } catch { toast.error("Could not open file"); }
  };

  const canDelete = !["received", "verified", "flagged"].includes(r.status);

  return (
    <div className="border border-[var(--cc-border)] rounded-xl p-4" data-testid={`reference-row-${idx}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-sm">{r.name}</div>
          <div className="text-xs text-[var(--cc-text-soft)]">{r.relationship} · {r.email}</div>
          {r.deadline && <div className="text-[10px] text-[var(--cc-text-soft)] mt-0.5">Deadline: {fmtDate(r.deadline)}</div>}
        </div>
        <div className="flex items-start gap-2">
          <div className="flex flex-col items-end gap-1">
            <span className={`pill ${refsPillClass(r.status)}`}>{r.status}</span>
            {r.assessment && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: VERDICT_BADGE[r.assessment.verdict]?.bg, color: VERDICT_BADGE[r.assessment.verdict]?.fg }}>
                {VERDICT_BADGE[r.assessment.verdict]?.label}
              </span>
            )}
          </div>
          {canDelete && (
            <button
              className="text-[var(--cc-text-soft)] hover:text-[var(--cc-accent)] p-1 -mt-0.5"
              title="Remove this referee"
              onClick={removeRef}
              data-testid={`ref-delete-${idx}`}
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {fr && (
        <div className="mt-3">
          <button className="btn-ghost text-xs" onClick={() => setExpanded(!expanded)} data-testid={`ref-toggle-${idx}`}>
            {expanded ? "Hide" : "View"} referee submission
          </button>
        </div>
      )}

      {expanded && fr && (
        <div className="mt-3 p-3 rounded-lg bg-[var(--cc-surface)] text-xs space-y-2" data-testid={`ref-submission-${idx}`}>
          <Row k="Submitted" v={fmtDateTime(fr.submitted_at)} />
          <Row k="Referee role" v={`${fr.referee_position} · ${fr.referee_company}`} />
          <Row k="Relationship" v={`${fr.relationship.replace(/_/g, " ")}${fr.relationship_other ? ` (${fr.relationship_other})` : ""}`} />
          <Row k="Length known" v={fr.length_known.replace(/_/g, " ")} />
          <Row k="Vulnerable adults" v={fr.work_with_elderly_vulnerable} />
          <Row k="Children" v={fr.work_with_children} />
          <div>
            <div className="font-semibold mt-2">Competency ratings</div>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {["timekeeping","attendance","sickness","work_ability","trustworthiness","conduct","honesty"].map((k) => (
                <div key={k} className="flex justify-between"><span className="capitalize text-[var(--cc-text-soft)]">{k.replace(/_/g," ")}</span><span className="font-mono font-semibold">{fr[`rating_${k}`]}/5</span></div>
              ))}
            </div>
          </div>
          <Row k="Re-employ?" v={fr.would_reemploy.replace(/_/g, " ")} />
          <Row k="Reason for leaving" v={fr.reason_for_leaving} />
          <Row k="Disciplinary" v={fr.disciplinary_action ? `Yes — ${fr.disciplinary_details}` : "No"} />
          <Row k="Safeguarding" v={fr.safeguarding_concerns ? `Yes — ${fr.safeguarding_details}` : "No"} />
          <div>
            <div className="font-semibold mt-2">Responsibilities</div>
            <div className="text-[var(--cc-text-soft)]">{fr.responsibilities}</div>
          </div>
          <div>
            <div className="font-semibold mt-2">Strengths</div>
            <div className="text-[var(--cc-text-soft)]">{fr.strengths}</div>
          </div>
          <div>
            <div className="font-semibold mt-2">Additional comments</div>
            <div className="text-[var(--cc-text-soft)]">{fr.additional_comments}</div>
          </div>
          <Row k="Signed by" v={`${fr.signature_name} on ${fr.signed_on}`} />
        </div>
      )}

      {/* HR Admin assessment block — only when referee submitted */}
      {r.status === "received" || r.status === "verified" || r.status === "flagged" ? (
        <div className="mt-3 p-3 rounded-lg border border-dashed border-[var(--cc-border)]" data-testid={`ref-assess-${idx}`}>
          <div className="label-eyebrow mb-2">HR assessment</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select className="cc-select" value={verdict} onChange={(e) => setVerdict(e.target.value)} data-testid={`ref-verdict-${idx}`}>
              <option value="">— Verdict —</option>
              <option value="acceptable">Acceptable</option>
              <option value="concerns">Concerns raised</option>
              <option value="unsatisfactory">Unsatisfactory</option>
            </select>
            <select className="cc-select" value={action} onChange={(e) => setAction(e.target.value)} data-testid={`ref-action-${idx}`}>
              <option value="none">No action required</option>
              <option value="flag_for_discussion">Flag for discussion</option>
              <option value="call_referee">Call referee directly</option>
              <option value="request_more_info">Request additional information</option>
            </select>
          </div>
          <textarea className="cc-textarea min-h-[60px] mt-2" placeholder="Internal HR comments (not visible to candidate)"
                    value={assessComment} onChange={(e) => setAssessComment(e.target.value)}
                    data-testid={`ref-assess-comments-${idx}`} />
          <div className="flex justify-end mt-2">
            <button className="btn-primary text-xs" onClick={submitAssessment} disabled={busy} data-testid={`ref-assess-submit-${idx}`}>
              {busy ? "Saving…" : (r.assessment ? "Update assessment" : "Save assessment")}
            </button>
          </div>
          {r.assessment && (
            <div className="text-[10px] text-[var(--cc-text-soft)] mt-2">
              Last assessed by {r.assessment.assessed_by} on {fmtDateTime(r.assessment.assessed_at)}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <select className="cc-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="awaiting">Awaiting</option>
            <option value="received">Received</option>
            <option value="verified">Verified</option>
            <option value="flagged">Flagged</option>
          </select>
          <select className="cc-select" value={reemploy === null ? "" : reemploy ? "yes" : "no"} onChange={(e) => setReemploy(e.target.value === "" ? null : e.target.value === "yes")}>
            <option value="">Re-employ? —</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-3 flex-wrap">
        <button className="btn-ghost text-xs" onClick={requestRef} data-testid={`ref-resend-${idx}`}>
          {r.form_token ? "Re-send request email" : "Send request email"}
        </button>
        <button className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => setShowUpload((s) => !s)} data-testid={`ref-upload-toggle-${idx}`}>
          <Upload className="w-3.5 h-3.5" /> {showUpload ? "Cancel upload" : "Upload email reply"}
        </button>
        {!fr && (
          <button className="btn-primary text-xs" onClick={save}>Save</button>
        )}
      </div>

      {showUpload && (
        <div className="mt-3 p-3 rounded-lg bg-[var(--cc-surface)] space-y-2" data-testid={`ref-upload-form-${idx}`}>
          <div className="text-[11px] text-[var(--cc-text-soft)]">
            Use this when the referee replied by email instead of using the portal form. The reference will be marked as received and the file stored securely.
          </div>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.eml,.msg,.txt"
            className="text-xs"
            onChange={(e) => setUpFile(e.target.files?.[0] || null)}
            data-testid={`ref-upload-file-${idx}`}
          />
          <textarea
            className="cc-input text-xs"
            placeholder="Optional comments (e.g. 'Referee replied confirming dates & re-employ Yes')"
            value={upComments}
            onChange={(e) => setUpComments(e.target.value)}
            rows={2}
            data-testid={`ref-upload-comments-${idx}`}
          />
          <div className="flex justify-end gap-2">
            <button className="btn-ghost text-xs" onClick={() => { setShowUpload(false); setUpFile(null); setUpComments(""); }}>Cancel</button>
            <button className="btn-primary text-xs" disabled={upBusy} onClick={uploadManual} data-testid={`ref-upload-submit-${idx}`}>
              {upBusy ? "Uploading…" : "Upload & mark received"}
            </button>
          </div>
        </div>
      )}

      {manualEvidence.length > 0 && (
        <div className="mt-3 p-2.5 rounded-lg border border-[var(--cc-border)] space-y-1.5" data-testid={`ref-manual-evidence-${idx}`}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--cc-text-soft)] inline-flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Email reference{manualEvidence.length > 1 ? `s (${manualEvidence.length})` : ""}
            </div>
            {isEmailUpload && <span className="pill pill-info text-[10px]">via email upload</span>}
          </div>
          {manualEvidence.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0">
                <div className="font-medium truncate">{e.file_name}</div>
                <div className="text-[10px] text-[var(--cc-text-soft)]">
                  Uploaded {fmtDateTime(e.uploaded_at)} by {e.uploaded_by_name || "HR"}
                  {e.comments && <> · "{e.comments}"</>}
                </div>
              </div>
              <button className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0" onClick={() => openManual(e.id)} data-testid={`ref-open-${idx}-${e.id}`}>
                <ExternalLink className="w-3 h-3" /> Open
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="text-[var(--cc-text-soft)] min-w-[140px]">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function OfferTab({ c, onRefresh }) {
  const o = c.offer;
  const [showNew, setShowNew] = useState(false);
  const [salary, setSalary] = useState(c.job_title ? "" : "");
  const [startDate, setStartDate] = useState("");
  const [type, setType] = useState("conditional");
  const [contractType, setContractType] = useState("permanent");
  const [managerName, setManagerName] = useState("");
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wdReason, setWdReason] = useState("conditions_not_met");
  const [wdNotes, setWdNotes] = useState("");
  const [wdNotify, setWdNotify] = useState(true);
  const [wdBusy, setWdBusy] = useState(false);

  const HOURLY = ["zero_hours", "bank", "relief", "variable_hours", "casual"];
  const isHourly = HOURLY.includes(contractType);

  const createOffer = async () => {
    if (!salary || !startDate) return toast.error("Salary and start date required");
    try {
      const conds = [
        { label: "Enhanced DBS clear", type: "dbs", due_date: new Date(Date.now() + 21 * 86400000).toISOString() },
        { label: "3 references verified", type: "references", due_date: new Date(Date.now() + 14 * 86400000).toISOString() },
        { label: "Right to Work verified", type: "rtw", due_date: new Date(Date.now() + 7 * 86400000).toISOString() },
        { label: "Medical clearance", type: "medical", due_date: new Date(Date.now() + 28 * 86400000).toISOString() },
      ];
      await api.post(`/candidates/${c.id}/offer`, {
        type,
        salary,
        start_date: new Date(startDate).toISOString(),
        conditions: type === "conditional" ? conds : [],
        contract_type: contractType,
        manager_name: managerName || undefined,
      });
      toast.success("Offer sent");
      setShowNew(false);
      onRefresh();
    } catch { toast.error("Could not create offer"); }
  };

  const updateCondition = async (cid, newStatus) => {
    try {
      await api.patch(`/candidates/${c.id}/offer/conditions/${cid}`, { status: newStatus });
      toast.success("Condition updated");
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  const submitWithdraw = async () => {
    setWdBusy(true);
    try {
      const { data } = await api.post(`/candidates/${c.id}/offer/withdraw`, {
        reason: wdReason,
        notes: wdNotes,
        notify_candidate: wdNotify,
      });
      toast.success(wdNotify
        ? (data.email_sent ? "Offer withdrawn — candidate notified" : "Offer withdrawn (email not sent)")
        : "Offer withdrawn — no email sent");
      setShowWithdraw(false);
      setWdNotes("");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to withdraw offer");
    } finally { setWdBusy(false); }
  };

  if (!o || showNew) {
    return (
      <Card title={o ? "Replace offer" : "Create offer"}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1">Type</label>
            <select className="cc-select" value={type} onChange={(e) => setType(e.target.value)} data-testid="offer-type-select">
              <option value="conditional">Conditional</option>
              <option value="final">Final</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Contract type</label>
            <select className="cc-select" value={contractType} onChange={(e) => setContractType(e.target.value)} data-testid="offer-contract-type-select">
              <option value="permanent">Permanent</option>
              <option value="fixed_term">Fixed-term</option>
              <option value="zero_hours">Zero-hours</option>
              <option value="bank">Bank</option>
              <option value="relief">Relief</option>
              <option value="variable_hours">Variable hours</option>
              <option value="casual">Casual</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">{isHourly ? "Hourly rate (write it as it should appear, e.g. '£14 per hour')" : "Annual salary"}</label>
            <input className="cc-input" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder={isHourly ? "£14 per hour" : "£26,000"} data-testid="offer-salary-input" />
            {isHourly && <div className="text-[10px] text-[var(--cc-text-soft)] mt-1">Zero-hours / bank / relief contracts use this hourly rate verbatim in the letter. No annual leave clause will be added — statutory accrual only.</div>}
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Start date</label>
            <input type="date" className="cc-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Manager name (optional)</label>
            <input className="cc-input" value={managerName} onChange={(e) => setManagerName(e.target.value)} placeholder="Sarah Manager" data-testid="offer-manager-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          {o && <button className="btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>}
          <button className="btn-primary" onClick={createOffer} data-testid="send-offer-button">Send Offer</button>
        </div>
      </Card>
    );
  }

  const allMet = o.conditions?.every((c) => c.status === "met");
  const withdrawn = o.status === "withdrawn";
  const canWithdraw = !withdrawn && o.status !== "declined";
  const PDF_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/candidates/${c.id}/offer/pdf`;

  return (
    <div className="space-y-5">
      <Card title="Offer Details" action={
        <div className="flex gap-2 flex-wrap">
          {!withdrawn && (
            <>
              <a className="btn-ghost text-xs" href={`${PDF_BASE}?template=conditional`} target="_blank" rel="noreferrer" data-testid="offer-pdf-conditional-link">
                <Download className="w-3.5 h-3.5" /> Conditional PDF
              </a>
              <a className="btn-ghost text-xs" href={`${PDF_BASE}?template=final`} target="_blank" rel="noreferrer" data-testid="offer-pdf-final-link">
                <Download className="w-3.5 h-3.5" /> Final PDF
              </a>
            </>
          )}
          {canWithdraw && (
            <button className="btn-ghost text-xs" style={{ color: "var(--cc-accent)" }}
              onClick={() => setShowWithdraw(true)} data-testid="withdraw-offer-button">
              <XCircle className="w-3.5 h-3.5" /> Withdraw Offer
            </button>
          )}
          {!withdrawn && o.status !== "declined" && <button className="btn-ghost text-xs" onClick={() => setShowNew(true)}>Replace</button>}
          {(withdrawn || o.status === "declined") && (
            <button
              className="btn-primary text-xs inline-flex items-center gap-1"
              onClick={() => setShowNew(true)}
              data-testid="reoffer-button"
              title="Send a new offer to this candidate"
            >
              <Plus className="w-3.5 h-3.5" /> Re-offer candidate
            </button>
          )}
        </div>
      }>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div><div className="label-eyebrow mb-1">Type</div><span className="capitalize font-semibold">{o.type}</span></div>
          <div><div className="label-eyebrow mb-1">Status</div>
            <span className={`pill ${o.status === "accepted" ? "pill-success" : (o.status === "declined" || withdrawn) ? "pill-danger" : "pill-warning"}`} data-testid="offer-status-pill">{o.status}</span>
          </div>
          <div><div className="label-eyebrow mb-1">Salary</div><span className="font-semibold">{o.salary}</span></div>
          <div><div className="label-eyebrow mb-1">Start date</div><span>{fmtDate(o.start_date)}</span></div>
          <div><div className="label-eyebrow mb-1">Sent</div><span>{fmtDateTime(o.sent_at)}</span></div>
          <div><div className="label-eyebrow mb-1">Response</div><span>{o.responded_at ? fmtDateTime(o.responded_at) : "—"}</span></div>
        </div>
      </Card>

      {!withdrawn && <CustomTemplateOfferCard candidate={c} offer={o} onRefresh={onRefresh} />}

      {o.status === "accepted" && !c.employee_id && (
        <PromoteToEmployeeCard candidate={c} onRefresh={onRefresh} />
      )}
      {o.status === "accepted" && (
        <ExternalSystemsCard candidate={c} onRefresh={onRefresh} />
      )}
      {c.employee_id && (
        <Card title="Employee Profile">
          <div className="text-sm flex items-center justify-between">
            <span>This candidate has been promoted to an employee.</span>
            <Link to={`/app/employees/${c.employee_id}`} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="open-employee-profile">
              <ExternalLink className="w-3.5 h-3.5" /> Open profile
            </Link>
          </div>
        </Card>
      )}

      {withdrawn && o.withdrawal && (
        <Card title="Withdrawal Record">
          <div className="grid grid-cols-2 gap-4 text-sm" data-testid="offer-withdrawal-record">
            <div><div className="label-eyebrow mb-1">Withdrawn at</div><span>{fmtDateTime(o.withdrawal.withdrawn_at)}</span></div>
            <div><div className="label-eyebrow mb-1">By</div><span>{o.withdrawal.withdrawn_by}</span></div>
            <div className="col-span-2"><div className="label-eyebrow mb-1">Reason</div><span>{o.withdrawal.reason_label}</span></div>
            {o.withdrawal.notes && <div className="col-span-2"><div className="label-eyebrow mb-1">Notes</div><span>{o.withdrawal.notes}</span></div>}
            <div><div className="label-eyebrow mb-1">Candidate notified</div><span>{o.withdrawal.candidate_notified ? "Yes" : "No"}</span></div>
            <div><div className="label-eyebrow mb-1">Notification sent at</div><span>{o.withdrawal.notification_sent_at ? fmtDateTime(o.withdrawal.notification_sent_at) : "—"}</span></div>
          </div>
          {c.purge_at && (
            <div className="mt-3 p-2 rounded-lg text-xs" style={{ background: "#fef3f2", color: "#7a271a" }}>
              GDPR: candidate record scheduled for purge on {fmtDate(c.purge_at)}.
            </div>
          )}
        </Card>
      )}

      {showWithdraw && (
        <Modal onClose={() => !wdBusy && setShowWithdraw(false)} testId="withdraw-offer-modal" widthClass="max-w-lg" zIndex={70} scrollable={false}>
          <h3 className="font-display text-xl font-bold mb-2">Withdraw Offer?</h3>
          <p className="text-sm text-[var(--cc-text-soft)] mb-4">
            This will mark the offer as withdrawn and (if selected) email the candidate. The candidate record will be scheduled for GDPR deletion in 6 months.
          </p>
          <label className="label-eyebrow block mb-1">Withdrawal Reason</label>
          <select className="cc-select mb-3" value={wdReason} onChange={(e) => setWdReason(e.target.value)} data-testid="withdraw-reason-select">
            <option value="conditions_not_met">Conditions Not Met</option>
            <option value="position_filled">Position Filled</option>
            <option value="candidate_request">Candidate Request</option>
            <option value="budget_restructure">Budget / Restructure</option>
            <option value="performance_concerns">Performance Concerns</option>
            <option value="other">Other</option>
          </select>
          <label className="label-eyebrow block mb-1">Additional Notes (max 500 chars, optional)</label>
          <textarea className="cc-textarea min-h-[80px] mb-3" maxLength={500}
            value={wdNotes} onChange={(e) => setWdNotes(e.target.value)} data-testid="withdraw-notes-textarea" />
          <label className="flex items-center gap-2 text-sm cursor-pointer mb-4">
            <input type="checkbox" checked={wdNotify} onChange={(e) => setWdNotify(e.target.checked)} data-testid="withdraw-notify-checkbox" />
            Send notification email to candidate
          </label>
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setShowWithdraw(false)} disabled={wdBusy} data-testid="withdraw-cancel-button">Cancel</button>
            <button className="btn-primary" style={{ background: "var(--cc-accent)" }}
              onClick={submitWithdraw} disabled={wdBusy} data-testid="withdraw-confirm-button">
              {wdBusy ? "Withdrawing…" : "Withdraw Offer"}
            </button>
          </div>
        </Modal>
      )}

      {o.conditions?.length > 0 && (
        <Card title="Conditions Tracker">
          {allMet && o.type === "conditional" && (
            <div className="mb-3 p-3 rounded-lg flex items-center justify-between" style={{ background: "#e8f0ea" }}>
              <span className="text-sm text-[#1f4d36] font-medium">All conditions met. Final offer auto-generated.</span>
              <ArrowRight className="w-4 h-4 text-[#1f4d36]" />
            </div>
          )}
          <div className="space-y-2">
            {o.conditions.map((cond) => {
              const days = daysUntil(cond.due_date);
              const danger = cond.status === "pending" && days <= 3;
              return (
                <div key={cond.id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--cc-border)]">
                  <div className="flex items-center gap-3">
                    {cond.status === "met" ? <CheckCircle2 className="w-5 h-5 text-[var(--cc-success)]" /> : <Circle className="w-5 h-5 text-[var(--cc-text-soft)]" />}
                    <div>
                      <div className="text-sm font-medium">{cond.label}</div>
                      <div className={`text-xs ${danger ? "text-[var(--cc-accent)]" : "text-[var(--cc-text-soft)]"}`}>
                        Due {fmtDate(cond.due_date)} · {days} days remaining
                      </div>
                    </div>
                  </div>
                  <select className="cc-select w-32 py-1 text-xs" value={cond.status} onChange={(e) => updateCondition(cond.id, e.target.value)}>
                    <option value="pending">Pending</option>
                    <option value="met">Met</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function OnboardingTab({ c, onRefresh }) {
  const tasks = c.onboarding || [];
  const done = tasks.filter((t) => t.completed).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const cats = [...new Set(tasks.map((t) => t.category))];

  const toggle = async (id, val) => {
    try {
      await api.patch(`/candidates/${c.id}/onboarding/${id}`, { completed: val });
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  return (
    <Card title={`Onboarding Checklist (${c.rtw?.nationality_status === "immigrant" ? "Immigrant" : "British"} path)`}>
      <div className="flex items-center gap-4 mb-5">
        <div className="flex-1">
          <div className="cc-progress"><div className="cc-progress-fill" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="text-sm font-semibold">{done}/{tasks.length} · {pct}%</div>
      </div>
      {cats.map((cat) => (
        <div key={cat} className="mb-4">
          <div className="label-eyebrow mb-2">{cat}</div>
          <div className="space-y-1">
            {tasks.filter((t) => t.category === cat).map((t) => (
              <label key={t.id} className="flex items-center gap-3 px-2 py-2 rounded hover:bg-[var(--cc-surface)] cursor-pointer">
                <input type="checkbox" checked={t.completed} onChange={(e) => toggle(t.id, e.target.checked)} data-testid={`onboard-task-${t.id}`} />
                <span className={`text-sm ${t.completed ? "line-through text-[var(--cc-text-soft)]" : ""}`}>{t.label}</span>
                {t.completed_at && <span className="text-xs text-[var(--cc-text-soft)] ml-auto">{fmtDate(t.completed_at)}</span>}
              </label>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function NotesTab({ c, onRefresh }) {
  const [text, setText] = useState("");
  const submit = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/candidates/${c.id}/notes`, { text });
      setText("");
      toast.success("Note added");
      onRefresh();
    } catch { toast.error("Failed"); }
  };
  return (
    <Card title="Internal Notes">
      <textarea className="cc-textarea min-h-[80px] mb-3" placeholder="Add a note (visible to all internal staff)…" value={text} onChange={(e) => setText(e.target.value)} data-testid="note-textarea" />
      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={submit} data-testid="add-note-button">Add Note</button>
      </div>
      <div className="space-y-3">
        {[...(c.notes || [])].reverse().map((n, i) => (
          <div key={i} className="p-3 rounded-lg bg-[var(--cc-surface)]">
            <div className="text-sm">{n.text}</div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">{n.by} · {fmtDateTime(n.at)}</div>
          </div>
        ))}
        {!c.notes?.length && <p className="text-sm text-[var(--cc-text-soft)]">No notes yet.</p>}
      </div>
    </Card>
  );
}



// =================================================================
//  Custom offer-letter template generation (Iteration 13 Phase B)
// =================================================================
function CustomTemplateOfferCard({ candidate, offer, onRefresh }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailIt, setEmailIt] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [history, setHistory] = useState([]);

  const offerType = offer.type || "conditional";

  const loadTemplates = useCallback(async () => {
    try {
      // Pull ALL templates so HR Admin can pick from any uploaded one. When the
      // candidate is on a Final offer we still PREFER final-type templates but
      // gracefully fall back to whatever is available — the backend doesn't
      // care which type was uploaded; the merge context is generated from the
      // candidate + offer payload at generation time.
      const { data } = await api.get(`/offer-templates`);
      const all = data || [];
      const matching = all.filter((t) => t.type === offerType);
      const ordered = [
        ...matching,
        ...all.filter((t) => t.type !== offerType),
      ];
      setTemplates(ordered);
      if (!selectedId && ordered[0]) setSelectedId(ordered[0].id);
    } catch { /* silent */ }
  }, [offerType, selectedId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${candidate.id}/generated-offers`);
      setHistory(data || []);
    } catch { /* silent */ }
  }, [candidate.id]);

  useEffect(() => { loadTemplates(); loadHistory(); }, [loadTemplates, loadHistory]);

  const generate = async () => {
    if (!selectedId) return toast.error("Select a template first");
    setBusy(true);
    try {
      const payload = {
        template_id: selectedId,
        offer: {
          salary: offer.salary,
          start_date: offer.start_date,
          dbs_deadline: offer.dbs_deadline || offer.start_date,
          references_deadline: offer.references_deadline || offer.start_date,
          rtw_deadline: offer.rtw_deadline || offer.start_date,
          medical_deadline: offer.medical_deadline,
        },
        send_email_to_candidate: emailIt,
      };
      const { data } = await api.post(
        `/candidates/${candidate.id}/offer/generate-from-template`,
        payload,
      );
      toast.success(
        emailIt
          ? (data.email_status === "sent" ? "PDF generated and emailed to candidate" : "PDF generated (email failed)")
          : "PDF generated",
      );
      await loadHistory();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  return (
    <Card title="Custom Offer Letter (template)" action={
      <Link to="/app/admin/offer-templates" className="btn-ghost text-xs" data-testid="manage-offer-templates-link">
        <ExternalLink className="w-3.5 h-3.5" /> Manage templates
      </Link>
    }>
      {templates.length === 0 ? (
        <div className="text-sm text-[var(--cc-text-soft)]" data-testid="no-offer-templates-msg">
          No offer letter templates uploaded yet.{" "}
          <Link to="/app/admin/offer-templates" className="underline">Upload one</Link> to enable custom-letter generation.
        </div>
      ) : (
        <>
          {(() => {
            const matchingCount = templates.filter((t) => t.type === offerType).length;
            if (matchingCount === 0) {
              return (
                <div className="text-[11px] mb-2 px-2 py-1.5 rounded bg-[#fdf3ec] border border-[var(--cc-accent-soft,#f0d6c3)] text-[var(--cc-accent)] flex items-center gap-1.5" data-testid="template-type-mismatch-hint">
                  <span>Heads-up: no <strong>{offerType}</strong>-type templates uploaded yet — your existing templates are listed below so you can still generate the letter.</span>
                </div>
              );
            }
            return null;
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label-eyebrow block mb-1">Template for {offerType} offer</label>
              <select
                className="cc-select"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                data-testid="custom-offer-template-select"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.type !== offerType ? `· ${t.type}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                className="btn-secondary text-xs inline-flex items-center gap-1.5"
                onClick={() => setShowPreview(true)}
                disabled={!selectedId}
                data-testid="custom-offer-preview-button"
              >
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox" checked={emailIt}
                  onChange={(e) => setEmailIt(e.target.checked)}
                  data-testid="custom-offer-email-checkbox"
                />
                Email PDF to candidate
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              onClick={generate}
              disabled={busy || !selectedId}
              data-testid="generate-custom-offer-pdf"
            >
              {busy ? "Generating…" : "Generate PDF"}
            </button>
          </div>

          {history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--cc-border)]">
              <div className="label-eyebrow mb-2">Recent generations</div>
              <div className="space-y-1.5">
                {history.slice(0, 4).map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs" data-testid={`generated-offer-${h.id}`}>
                    <div>
                      <span className="font-medium">{h.template_name || "—"}</span>
                      <span className="text-[var(--cc-text-soft)] ml-2">
                        {h.generated_at ? fmtDateTime(h.generated_at) : ""} · {(h.pdf_size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <a
                      className="btn-ghost text-xs"
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/generated-offers/${h.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid={`download-generated-offer-${h.id}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showPreview && selectedId && (
            <TemplatePreviewModal
              templateId={selectedId}
              candidateId={candidate.id}
              offer={offer}
              onClose={() => setShowPreview(false)}
            />
          )}
        </>
      )}
    </Card>
  );
}

function TemplatePreviewModal({ templateId, candidateId, offer, onClose }) {
  const [paragraphs, setParagraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.post(`/offer-templates/${templateId}/preview`, {
      candidate_id: candidateId,
      offer: {
        salary: offer.salary, start_date: offer.start_date,
        dbs_deadline: offer.dbs_deadline || offer.start_date,
        references_deadline: offer.references_deadline || offer.start_date,
        rtw_deadline: offer.rtw_deadline || offer.start_date,
        medical_deadline: offer.medical_deadline,
      },
    }).then(({ data }) => {
      if (cancelled) return;
      setParagraphs(data.preview_paragraphs || []);
    }).catch((e) => {
      if (cancelled) return;
      setError(e.response?.data?.detail || "Preview failed");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [templateId, candidateId, offer]);

  return (
    <Modal onClose={onClose} testId="candidate-offer-preview-modal" widthClass="max-w-3xl" zIndex={70}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold">Offer letter preview</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="border border-[var(--cc-border)] rounded-lg bg-white p-6 max-h-[60vh] overflow-y-auto">
        {loading && <div className="text-center py-6 text-[var(--cc-text-soft)]">Rendering…</div>}
        {error && <div className="text-[var(--cc-accent)] text-sm">{error}</div>}
        {!loading && !error && paragraphs.map((p, i) => (
          <p key={i} className="mb-2 text-sm leading-relaxed whitespace-pre-wrap">{p}</p>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <button className="btn-primary text-sm" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}



// ====================================================================
//  Promote candidate to employee (Phase 1 — Employee Profile module)
// ====================================================================
function PromoteToEmployeeCard({ candidate, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const promote = async () => {
    if (!window.confirm("Generate an employee profile for this candidate? An invite email will be sent so they can log in to their portal.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${candidate.id}/promote-to-employee`);
      setResult(data);
      toast.success(data.already_exists ? "Employee profile already exists" : "Employee profile created");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promotion failed");
    } finally { setBusy(false); }
  };
  return (
    <Card title="Generate Employee Profile">
      <div className="text-sm text-[var(--cc-text-soft)] mb-3">
        This candidate has accepted their offer. Click below to create their employee record (auto-populated from the recruitment data) and send them an invite email so they can log in to acknowledge policies.
      </div>
      <button
        className="btn-primary text-sm inline-flex items-center gap-1.5"
        onClick={promote}
        disabled={busy}
        data-testid="generate-employee-profile-button"
      >
        {busy ? "Generating…" : "Generate Employee Profile"}
      </button>
      {result?.employee && (
        <div className="mt-3 text-xs">
          ✅ Employee ID <code>{result.employee.id.slice(0,8)}</code>.{" "}
          <Link to={`/app/employees/${result.employee.id}`} className="underline">Open profile →</Link>
        </div>
      )}
    </Card>
  );
}


function ExternalSystemsCard({ candidate, onRefresh }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // 'careconnect' | null

  const load = async () => {
    try {
      const { data } = await api.get(`/candidates/${candidate.id}/external-sync-status`);
      setStatus(data);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidate.id]);

  const pushCareConnect = async () => {
    if (!window.confirm("Send this candidate to Care Connect as a new staff member? This triggers the registered webhook(s).")) return;
    setBusy("careconnect");
    try {
      const { data } = await api.post(`/candidates/${candidate.id}/care-connect/create-staff`);
      toast.success(`Sent to Care Connect (${data.queued} endpoint${data.queued === 1 ? "" : "s"})`);
      await load(); onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send to Care Connect failed");
    } finally { setBusy(null); }
  };

  const cc = status?.care_connect;
  const ccDone = !!cc?.dispatched;

  return (
    <Card title="Sync to external systems">
      <div className="text-sm text-[var(--cc-text-soft)] mb-3">
        Now this candidate has accepted, push them out to your other systems so HR setup is one click instead of duplicate data entry.
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="cc-card p-3" data-testid="care-connect-sync-card">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-medium inline-flex items-center gap-1.5"><Webhook className="w-4 h-4" /> Care Connect</div>
            {ccDone && <span className="pill pill-success">Sent</span>}
          </div>
          <div className="text-xs text-[var(--cc-text-soft)] mb-2.5">
            {ccDone
              ? <>Sent {fmtDateTime(cc.dispatched_at)} by {cc.dispatched_by || "HR"}.</>
              : <>Fires the <code>staff.create_intent</code> webhook to every Care Connect endpoint registered in <Link to="/app/admin/integrations" className="underline">Integrations</Link>.</>}
          </div>
          <button
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            onClick={pushCareConnect}
            disabled={busy === "careconnect" || ccDone}
            data-testid="push-care-connect-button"
          >
            {busy === "careconnect" ? "Sending…" : (ccDone ? "Already sent" : "Send to Care Connect")}
          </button>
        </div>

      </div>
    </Card>
  );
}
